import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { map } from 'lodash';
import { type MouseEvent, useState } from 'react';
import {
  useNotify, useRecordContext, useRefresh,
} from 'react-admin';
import { Else, If, Then } from 'react-if';

import type {
  ContactOutreachChannelChoice,
  ContactOutreachResourceOptions,
  IndustryContributor,
} from '@/Api/studioServerTypes';
import { authHttpClient } from '@/Auth/authProvider';
import { stringAvatar } from '@/Helper/stringAvatar';

export default function RecordOutreachDialog({ open, onClose }: { open: boolean; onClose: ()=> void; }): JSX.Element {
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext<IndustryContributor>();

  const { data: channelOptions = [], isLoading: isLoadingChannels, isError: hasChannelsError } = useQuery<ContactOutreachChannelChoice[]>({
    queryKey: ['contact-outreach-channels'],
    enabled: open,
    queryFn: async () => {
      const response = await authHttpClient('/api/contact-outreaches/channels');
      return response.json as ContactOutreachChannelChoice[];
    },
  });
  const {
    data: resourceOptions,
    isLoading: isLoadingResourceOptions, isError: hasResourceOptionsError,
  } = useQuery<ContactOutreachResourceOptions>({
    queryKey: ['contact-outreach-resource-options'],
    enabled: open,
    queryFn: async () => {
      const response = await authHttpClient('/api/contact-outreaches/resource-options');

      return response.json as ContactOutreachResourceOptions;
    },
  });
  const [channel, setChannel] = useState('');
  const [inbound, setInbound] = useState(false);
  const [resourceIdentifier, setResourceIdentifier] = useState('');
  const [saving, setSaving] = useState(false);
  const engineers = map(resourceOptions?.ResourceList, (Name, ResourceIdentifier) => ({
    ResourceIdentifier,
    Name: Name ?? '',
  }));
  const selectedResourceIdentifier = resourceIdentifier || engineers[0]?.ResourceIdentifier || '';
  const selectedChannel = channel || (channelOptions[0]?.Id ?? '').trim();

  const handleEngineerChange = (_event: MouseEvent<HTMLElement>, value: string | null): void => {
    if (typeof value === 'string') {
      setResourceIdentifier(value);
    }
  };

  const handleDirectionChange = (_event: MouseEvent<HTMLElement>, value: string | null): void => {
    if (value === 'inbound' || value === 'outbound') {
      setInbound(value === 'inbound');
    }
  };

  const handleClose = (): void => {
    if (saving) {
      return;
    }
    setChannel('');
    setInbound(false);
    setResourceIdentifier('');
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedChannel) {
      notify('Please select a channel', { type: 'warning', autoHideDuration: 3000 });
      return;
    }

    if (!selectedResourceIdentifier) {
      notify('Please select an engineer', { type: 'warning', autoHideDuration: 3000 });
      return;
    }
    setSaving(true);
    let hasRecorded = false;
    try {
      await authHttpClient('/api/contact-outreaches/create', {
        method: 'POST',
        body: JSON.stringify({
          RepresentedResourceIdentifier: selectedResourceIdentifier,
          ReachedContactIdentifier: record?.ContactIdentifier,
          Channel: selectedChannel,
          IsInbound: inbound,
        }),
      });

      notify('Outreach recorded', { type: 'success', autoHideDuration: 3000 });
      hasRecorded = true;
    } catch {
      notify('Failed to record outreach', { type: 'warning', autoHideDuration: 3000 });
    } finally {
      setSaving(false);
    }

    if (hasRecorded) {
      refresh();
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      onClose={handleClose}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Record Outreach
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Paper
          variant='outlined'
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 2,
          }}
        >
          <Stack
            spacing={0.75}
          >
            <Typography variant='subtitle2'>
              Direction
            </Typography>
            <ToggleButtonGroup
              exclusive
              size='small'
              color='secondary'
              value={inbound ? 'inbound' : 'outbound'}
              onChange={handleDirectionChange}
              sx={{
                alignSelf: 'flex-start',
                mt: 0.25,
              }}
            >
              <ToggleButton
                value='outbound'
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  px: 2,
                }}
              >
                Outbound
              </ToggleButton>
              <ToggleButton
                value='inbound'
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  px: 2,
                }}
              >
                Inbound
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        <Stack spacing={1}>
          <Box>
            <Typography variant='subtitle2'>
              Engineer
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Select the engineer to record this outreach under.
            </Typography>
          </Box>

          <If condition={isLoadingResourceOptions}>
            <Then>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1.5}
                sx={{ py: 3, color: 'text.secondary' }}
              >
                <CircularProgress size={20} />
                <Typography variant='body2'>
                  Loading engineers...
                </Typography>
              </Stack>
            </Then>
          </If>

          <If condition={!isLoadingResourceOptions && hasResourceOptionsError}>
            <Then>
              <Typography variant='body2' color='error'>
                Failed to load engineers.
              </Typography>
            </Then>
          </If>

          <If condition={!isLoadingResourceOptions && !hasResourceOptionsError && !engineers.length}>
            <Then>
              <Typography variant='body2' color='text.secondary'>
                No engineers are available for outreach right now.
              </Typography>
            </Then>
          </If>

          <If condition={!isLoadingResourceOptions && !hasResourceOptionsError && engineers.length > 0}>
            <Then>
              <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                <ToggleButtonGroup
                  exclusive
                  orientation='vertical'
                  value={selectedResourceIdentifier}
                  onChange={handleEngineerChange}
                  sx={{ width: '100%' }}
                >
                  {engineers.map((item) => {
                    const itemId = (item.ResourceIdentifier ?? '').trim();
                    const itemName = (item.Name ?? '').trim() || 'Your engineer';
                    const avatarProps = stringAvatar(itemName);

                    return (
                      <ToggleButton
                        key={itemId}
                        value={itemId}
                        color='secondary'
                        disabled={saving}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 1.25,
                          gap: 1.5,
                        }}
                      >
                        <Avatar
                          {...avatarProps}
                          sx={{
                            ...avatarProps.sx,
                            width: 36,
                            height: 36,
                            fontSize: 14,
                          }}
                        />
                        <Typography
                          variant='body2'
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            textAlign: 'left',
                            fontWeight: 500,
                          }}
                        >
                          {itemName}
                        </Typography>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </Box>
            </Then>
          </If>
        </Stack>

        <Stack spacing={1}>
          <Box>
            <Typography variant='subtitle2'>
              Channel
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Choose how the outreach happened.
            </Typography>
          </Box>

          <If condition={isLoadingChannels}>
            <Then>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1.5}
                sx={{ py: 1, color: 'text.secondary' }}
              >
                <CircularProgress size={20} />
                <Typography variant='body2'>
                  Loading outreach channels...
                </Typography>
              </Stack>
            </Then>
          </If>

          <If condition={!isLoadingChannels && hasChannelsError}>
            <Then>
              <Typography variant='body2' color='error'>
                Failed to load outreach channels.
              </Typography>
            </Then>
          </If>

          <If condition={!isLoadingChannels && !hasChannelsError && !channelOptions.length}>
            <Then>
              <Typography variant='body2' color='text.secondary'>
                No outreach channels are available right now.
              </Typography>
            </Then>
          </If>

          <If condition={!isLoadingChannels && !hasChannelsError && channelOptions.length > 0}>
            <Then>
              <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                <ButtonGroup
                  fullWidth
                  color='secondary'
                >
                  {channelOptions.map((item) => {
                    const itemId = (item.Id ?? '').trim();
                    const itemName = (item.Name ?? '').trim() || itemId;
                    const selected = itemId === selectedChannel;

                    return (
                      <Button
                        key={itemId}
                        variant={selected ? 'contained' : 'outlined'}
                        onClick={() => {
                          setChannel(itemId);
                        }}
                        disabled={saving}
                        sx={{
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {itemName}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </Box>
            </Then>
          </If>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={saving}
          color='secondary'
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='secondary'
          onClick={handleSave}
          startIcon={(
            <If condition={saving}>
              <Then>
                <CircularProgress size={16} color='inherit' />
              </Then>
            </If>
          )}
        >
          <If condition={saving}>
            <Then>Saving...</Then>
            <Else>Save</Else>
          </If>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
